import React, {useEffect, useMemo, useState} from 'react';
import {Box, Card, CircularProgress, DialogActions, DialogContent, DialogTitle, Link, Typography} from '@mui/material';
import {ContentCopy} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import Dialog from '../../../../../../components/base/dialog';
import Button from '../../../../../../components/base/button/button';
import IconButton from '../../../../../../components/base/button/icon_button';
import CopyToClipboard from '../../../../../../components/base/copy-to-clipboard';
import Api from '../../../../../../../core/services/api/api';
import useIsMounted from '../../../../../../hooks/use-is-mounted';

/**
 * Builds a mailto for the operator's mail client with the activation link in the body.
 * @param {string} email
 * @param {string} activationLink
 * @param {(key: string, options?: object) => string} t
 * @returns {string}
 */
const buildActivationMailto = (email, activationLink, t) => {
    const trimmed = (email ?? '').trim();
    if (!activationLink || !trimmed) {
        return '';
    }
    const subject = t('mailSubject');
    const body = t('mailBody', {link: activationLink});
    return `mailto:${trimmed}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

/**
 * Member name, falling back to the email when the name is empty.
 * @param {any} member
 * @returns {string}
 */
const memberLabel = (member) => {
    const name = `${member?.firstName ?? ''} ${member?.lastName ?? ''}`.trim();
    return name || member?.email || '';
};

/**
 * Loads a fresh activation link for one On Hold member, or for a selected batch.
 * @param {object} props
 * @param {boolean} props.open
 * @param {any} [props.member]
 * @param {any[]|null} [props.members]
 * @param {() => void} props.onClose
 */
const ResendActivationDialog = ({open, member, members, onClose}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.customers.members.resendActivation'});
    const isMounted = useIsMounted();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [batchRows, setBatchRows] = useState([]);
    const isBatch = open && Array.isArray(members) && members.length > 0;

    /**
     * Clears the previous link when the dialog closes so the next open starts on the loading state.
     */
    useEffect(() => {
        if (open) {
            return undefined;
        }
        setLoading(false);
        setResult(null);
        setBatchRows([]);
        return undefined;
    }, [open]);

    /**
     * Requests a new activation email and link whenever the dialog opens for a single member.
     */
    useEffect(() => {
        if (!open || isBatch || !member?.id) {
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setResult(null);

        const resend = async () => {
            const response = await Api.resendUserActivation(member.id);
            if (!isMounted() || cancelled) {
                return;
            }
            setLoading(false);
            if (response?.resultFlag) {
                setResult(response.data ?? {});
                return;
            }
            onClose();
        };

        void resend();
        return () => {
            cancelled = true;
        };
    }, [isBatch, isMounted, member?.id, onClose, open]);

    /**
     * Resends activation one selected member at a time and keeps each row's own result.
     */
    useEffect(() => {
        if (!isBatch) {
            return undefined;
        }
        let cancelled = false;
        setBatchRows(members.map((entry) => ({
            id: entry.id,
            member: entry,
            status: 'pending',
            activationLink: '',
            error: '',
        })));

        const resendAll = async () => {
            for (const entry of members) {
                if (cancelled || !isMounted()) {
                    return;
                }
                setBatchRows((prev) => prev.map((row) => (
                    row.id === entry.id ? {...row, status: 'loading', error: ''} : row
                )));
                const response = await Api.resendUserActivation(entry.id, {showErrorToast: false});
                if (cancelled || !isMounted()) {
                    return;
                }
                setBatchRows((prev) => prev.map((row) => {
                    if (row.id !== entry.id) {
                        return row;
                    }
                    const link = response?.data?.activationLink ?? '';
                    if (response?.resultFlag && link) {
                        return {
                            ...row,
                            status: 'success',
                            activationLink: link,
                            error: '',
                        };
                    }
                    return {
                        ...row,
                        status: 'error',
                        activationLink: '',
                        error: response?.message || t('batchRowError'),
                    };
                }));
            }
        };

        void resendAll();
        return () => {
            cancelled = true;
        };
    }, [isBatch, isMounted, members, open, t]);

    const activationLink = result?.activationLink ?? '';
    const showLoading = !isBatch && (loading || !result);

    /**
     * Mailto for the single-member dialog.
     * @type {string}
     */
    const mailtoHref = useMemo(() => (
        buildActivationMailto(member?.email, activationLink, t)
    ), [activationLink, member?.email, t]);

    return (
        <Dialog
            open={open}
            setOpen={onClose}
            maxWidth={isBatch ? 'md' : 'sm'}
            className={isBatch ? 'bound-dialog' : ''}
        >
            <DialogTitle component="div">
                {
                    isBatch
                        ? <Typography variant="h6" sx={{p: 2}}>{t('batchTitle')}</Typography>
                        : t('title')
                }
            </DialogTitle>
            <DialogContent dividers>
                {
                    showLoading &&
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4}}>
                        <CircularProgress color="secondary"/>
                        <Typography variant="body2" color="text.secondary">
                            {t('loading')}
                        </Typography>
                    </Box>
                }
                {
                    !isBatch && !showLoading && !!result &&
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, py: 1}}>
                        {
                            result.emailSent &&
                            <Typography variant="body1">
                                {t('emailSent', {email: member?.email ?? ''})}
                            </Typography>
                        }
                        {
                            !!activationLink &&
                            <ActivationLinkBlock
                                activationLink={activationLink}
                                email={member?.email}
                                t={t}
                            />
                        }
                        <Typography variant="body2" color="text.secondary">
                            {t('explanation')}
                        </Typography>
                    </Box>
                }
                {
                    isBatch &&
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5, p: 2}}>
                        {batchRows.map((row) => (
                            <BatchMemberRow key={row.id} row={row} t={t}/>
                        ))}
                    </Box>
                }
            </DialogContent>
            <DialogActions sx={{p: 0, m: 0, display: 'flex', justifyContent: 'space-between'}}>
                <Button
                    variant="dialog"
                    borderRadius="0 8px 0 8px"
                    color="primary"
                    onClick={onClose}
                >
                    {t('close')}
                </Button>
                {
                    !isBatch && !!mailtoHref &&
                    <Button
                        variant="dialog"
                        borderRadius="8px 0 8px 0"
                        color="primary"
                        component="a"
                        href={mailtoHref}
                    >
                        {t('openInbox')}
                    </Button>
                }
            </DialogActions>
        </Dialog>
    );
};

/**
 * Copyable activation link with an Open Inbox mailto.
 * @param {object} props
 * @param {string} props.activationLink
 * @param {string} [props.email]
 * @param {boolean} [props.compact]
 * @param {(key: string, options?: object) => string} props.t
 */
const ActivationLinkBlock = ({activationLink, email, compact, t}) => {
    const mailtoHref = buildActivationMailto(email, activationLink, t);

    return (
        <Box sx={compact ? {display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1} : undefined}>
            {
                !compact &&
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    {t('linkLabel')}
                </Typography>
            }
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    minWidth: 0,
                    flex: compact ? 1 : undefined,
                    ...(compact ? {} : {
                        bgcolor: 'action.hover',
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                    }),
                }}
            >
                <Link
                    href={activationLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    noWrap
                    title={activationLink}
                    sx={{minWidth: 0, flex: 1}}
                >
                    {activationLink}
                </Link>
                <CopyToClipboard tooltipProps={{title: t('copied')}}>
                    {({copy}) => (
                        <IconButton
                            color="secondary"
                            aria-label={t('copy')}
                            onPressed={() => copy(activationLink)}
                        >
                            <ContentCopy fontSize="small"/>
                        </IconButton>
                    )}
                </CopyToClipboard>
                {
                    compact && !!mailtoHref &&
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        component="a"
                        href={mailtoHref}
                        sx={{flexShrink: 0}}
                    >
                        {t('openInbox')}
                    </Button>
                }
            </Box>
        </Box>
    );
};

/**
 * One compact batch result: identity, progress, link actions, or an error.
 * @param {object} props
 * @param {{member: any, status: string, activationLink: string, error: string}} props.row
 * @param {(key: string, options?: object) => string} props.t
 */
const BatchMemberRow = ({row, t}) => {
    const waiting = row.status === 'pending' || row.status === 'loading';

    return (
        <Card
            variant="outlined"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1.25,
                minWidth: 0,
            }}
        >
            <Box sx={{minWidth: 0, width: 200, flexShrink: 0}}>
                <Typography variant="body2" fontWeight={600} noWrap title={memberLabel(row.member)}>
                    {memberLabel(row.member)}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap title={row.member?.email ?? ''}>
                    {row.member?.email ?? ''}
                </Typography>
            </Box>
            {
                waiting &&
                <CircularProgress size={18} color="secondary"/>
            }
            {
                row.status === 'success' && !!row.activationLink &&
                <ActivationLinkBlock
                    compact
                    activationLink={row.activationLink}
                    email={row.member?.email}
                    t={t}
                />
            }
            {
                row.status === 'error' &&
                <Typography variant="caption" color="error">
                    {row.error}
                </Typography>
            }
        </Card>
    );
};

export default ResendActivationDialog;
